import { ArrowDropDown } from '@mui/icons-material';
import {
    Accordion,
    AccordionSummary,
    Box,
    Radio,
    Typography,
    alpha,
} from '@mui/material';
import { useField } from 'formik';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { isCategoryUncategorized } from 'entities/Category/lib/isCategoryUncategorized';
import { getUserCanEditCatalogCategories } from 'entities/User/model/selectors/userPermissionsSeletors';
import { CategoryContextMenu } from 'features/CategoryContextMenu';
import { CategoryForm } from 'features/CategoryForm';
import { CategoryFormMode } from 'features/CategoryForm/module/types/categoryFormsTypes';

interface AddCatalogItemCategoryProps {
    title: string;
    id: string;
    glCode: string;
    catalogId: string;
    color: string;
    subGroups?: any;
}

export const AddCatalogItemCategory = memo(
    ({
        title,
        glCode,
        id,
        subGroups,
        color,
        catalogId,
    }: AddCatalogItemCategoryProps) => {
        const { t } = useTranslation();
        const [{ value }, , { setValue }] = useField('category');
        const [editMode, setEditMode] = useState<boolean>(false);
        const canEditCategories = useSelector(getUserCanEditCatalogCategories);

        const onSetValue = () => {
            setValue(id);
        };

        const onOpen = useCallback(() => {
            setEditMode(true);
        }, []);

        const onClose = useCallback(() => {
            setEditMode(false);
        }, []);

        return (
            <Box
                data-testclass="category"
                position="relative"
                sx={{
                    '&:hover .context-menu-icon, & .context-menu-icon.is-open':
                        {
                            opacity: 1,
                        },
                }}
            >
                {!editMode ? (
                    <Accordion sx={{ overflow: 'unset', flex: 1 }}>
                        <AccordionSummary
                            sx={{
                                pl: '8px',
                                minHeight: 'unset !important',
                                height: 'auto',
                                '& .MuiAccordionSummary-content': {
                                    my: '8px',
                                    overflow: 'visible',
                                    '&.Mui-expanded': { my: '8px' },
                                },
                            }}
                            expandIcon={
                                subGroups ? (
                                    <ArrowDropDown
                                        sx={(theme) => ({
                                            color: alpha(
                                                theme.palette.common?.black,
                                                0.3,
                                            ),
                                        })}
                                    />
                                ) : undefined
                            }
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                flex="1"
                                gap="4px"
                                onClick={onSetValue}
                            >
                                <Radio
                                    data-testid="Category-radio-button"
                                    value={value}
                                    checked={value === id}
                                    name="category"
                                    sx={{
                                        p: 0,
                                        width: '24px',
                                        height: '24px',
                                        display: 'block',
                                    }}
                                />
                                {!isCategoryUncategorized(title) && (
                                    <Box
                                        height="8px"
                                        width="8px"
                                        bgcolor={color}
                                        flexShrink={0}
                                        borderRadius="50%"
                                    />
                                )}
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    ml="8px"
                                    flex={1}
                                >
                                    <Typography>{title}</Typography>
                                    <Typography
                                        flexShrink={0}
                                        ml="16px"
                                        color={(theme) =>
                                            alpha(
                                                theme.palette.common?.black,
                                                0.5,
                                            )
                                        }
                                    >
                                        {glCode || ''}
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionSummary>
                    </Accordion>
                ) : (
                    <CategoryForm
                        onClose={onClose}
                        mode={CategoryFormMode.EDIT}
                        catalogId={catalogId}
                        glCode={glCode}
                        categoryId={id}
                        formTitle={t('Edit Category')}
                        title={title}
                        color={color}
                    />
                )}
                {!editMode &&
                    !isCategoryUncategorized(title) &&
                    canEditCategories && (
                        <Box
                            position="absolute"
                            right="-33px"
                            top="50%"
                            sx={{
                                transform: 'translateY(-50%)',
                                cursor: 'pointer',
                            }}
                        >
                            <CategoryContextMenu onEdit={onOpen} />
                        </Box>
                    )}
            </Box>
        );
    },
);
